import LoadingSpiner from "@components/ui/LoadingSpiner";
import { type ReactNode } from "react";
import { X } from "lucide-react";
import { MSG } from "@constants/MSG.ts";
import useNurses from "@hooks/useNurses.ts";
import { useGroupSelection } from "@hooks/useGroupSelection.ts";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useTeamsQuery } from "@hooks/roster-craete/useTeamsQuery.ts";
import CustomModal from "@/components/modal/CustomModal";

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  isPending: boolean;
  pendingMsg?: string;
  title: string;
  children?: ReactNode;
  gradeStrategy: string;
  setGradeStrategy: (value: string) => void;
}

export default function RosterCreateModal({
  isOpen,
  onClose,
  isPending,
  pendingMsg = MSG.CONFIG_SETTING,
  title,
  children,
  gradeStrategy,
  setGradeStrategy,
}: UnsavedChangesModalProps) {
  const { selectedGroupId } = useGroupSelection();

  const isAdmin = useIsAdmin();

  const { data } = useNurses(isAdmin ? selectedGroupId : null);

  const { data: teamData } = useTeamsQuery(isAdmin ? selectedGroupId : null);

  const hasAnyMembers = !!teamData?.some(
    (team) => team.team_members.length > 0
  );
  const hasGrade = !!data?.some((nurse) => nurse.grade);

  const MODES = [
    { id: "default", key: "COMBINED", label: "기본 생성", disabled: false },
    {
      id: "team",
      key: "TEAM",
      label: "Team 우선 적용",
      disabled: !hasAnyMembers,
    },
    {
      id: "grade",
      key: "GRADE",
      label: "Grade 우선 적용",
      disabled: !hasGrade,
    },
  ];

  if (!isOpen) return null;

  return (
    <CustomModal.Panel
      onClose={onClose}
      dismissOnOverlay
      className="border-0 bg-transparent shadow-none"
    >
      <div className="-m-5 flex justify-center">
        <div className="bg-sig-gradient rounded-[12px] p-[5px]">
          <div
            className="relative flex min-w-[min(100%,320px)] flex-col items-center justify-center gap-8 rounded-[12px] bg-white px-[56px] py-[40px] shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="absolute right-2 top-2 flex size-7 cursor-pointer items-center justify-center rounded-sm bg-zinc-50 text-zinc-400 hover:text-zinc-700"
            >
              <X />
            </button>
            {isPending ? (
              <LoadingSpiner msg={pendingMsg} />
            ) : (
              <>
                <h3 className="mt-2 text-center font-pretendard text-[18px] font-semibold text-sig">
                  {title}
                </h3>

                <div className="flex gap-6">
                  {MODES.map((mode) => (
                    <label
                      key={mode.id}
                      className={`comp_inputRadio ${
                        mode.disabled
                          ? "text-gray-400 !cursor-not-allowed opacity-50"
                          : gradeStrategy === mode.key
                            ? "text-sig"
                            : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="create_mode"
                        checked={gradeStrategy === mode.key}
                        onChange={() =>
                          !mode.disabled && setGradeStrategy(mode.key)
                        }
                        disabled={mode.disabled}
                        className={mode.disabled ? "!cursor-not-allowed" : ""}
                      />
                      <span
                        className={`com_inputRadio_checkIcon ${mode.disabled ? "!cursor-not-allowed" : ""}`}
                      ></span>
                      {mode.label}
                    </label>
                  ))}
                </div>

                {children}
              </>
            )}
          </div>
        </div>
      </div>
    </CustomModal.Panel>
  );
}
